import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  RefreshCw, Loader2, Bot as BotIcon, CheckCircle2, XCircle, Plug,
  ChevronDown, ChevronRight, ArrowUpRight, ArrowDownRight, LogOut,
  Zap, Settings as SettingsIcon, AlertTriangle, Clock, Activity
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

// ─── helpers ───────────────────────────────────────────────────
const fmtCcy = (n) =>
  n == null ? "—" : n.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });
const fmtNum = (n, d = 2) =>
  n == null ? "—" : Number(n).toLocaleString("en-IN", { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtPct = (n) => (n == null ? "—" : `${Number(n).toFixed(2)}%`);
const fmtDateTime = (s) => {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch { return s; }
};

// SpeedBot's API returns `bot_status` as either 0/1 (int), "0"/"1" (str), or
// a descriptor like "running"/"active"/"inactive". Normalise here.
const ACTIVE_BOT_STATUSES = new Set(["1", "running", "active", "live", "on"]);
const isBotActive = (status) => {
  if (status == null) return false;
  if (typeof status === "boolean") return status;
  if (typeof status === "number") return status === 1;
  return ACTIVE_BOT_STATUSES.has(String(status).trim().toLowerCase());
};

const PnlText = ({ value, className = "" }) => {
  if (value == null) return <span className="text-zinc-500">—</span>;
  const pos = value >= 0;
  return (
    <span className={`font-mono font-semibold ${pos ? "text-emerald-400" : "text-rose-400"} ${className}`}>
      {pos ? "+" : ""}{fmtCcy(value)}
    </span>
  );
};

// ─── Bot Row with drill-down for positions/orders ──────────────
function BotRow({ account, bot, expanded, onToggle }) {
  return (
    <>
      <tr
        data-testid={`bot-row-${bot.id}`}
        onClick={onToggle}
        className="cursor-pointer hover:bg-zinc-900/70 transition-colors border-t border-zinc-800/60"
      >
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-2">
            {expanded ? <ChevronDown className="w-4 h-4 text-zinc-400" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
            <BotIcon className="w-4 h-4 text-zinc-400" />
            <div>
              <div className="text-sm font-semibold text-white flex items-center gap-1.5">
                <span
                  data-testid={`bot-status-dot-${bot.botid}`}
                  className={`w-2 h-2 rounded-full inline-block ${
                    isBotActive(bot.bot_status)
                      ? "bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.8)]"
                      : "bg-rose-500 shadow-[0_0_6px_rgba(244,63,94,0.8)]"
                  }`}
                  title={isBotActive(bot.bot_status) ? "Bot is running" : "Bot is inactive"}
                />
                <span>{bot.name || `Bot #${bot.botid}`}</span>
              </div>
              <div className="text-[11px] text-zinc-500 font-mono">Bot ID: {bot.botid}</div>
            </div>
          </div>
        </td>
        <td className="px-3 py-2.5 text-sm text-zinc-300">
          {(bot.instruments && bot.instruments.length > 0) ? bot.instruments.join(", ") : "—"}
        </td>
        <td className="px-3 py-2.5">
          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
            isBotActive(bot.bot_status)
              ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30"
              : "bg-rose-500/10 text-rose-300 border border-rose-500/30"
          }`}>
            {isBotActive(bot.bot_status) ? "Active" : "Inactive"}
          </span>
        </td>
        <td className="px-3 py-2.5 text-right"><PnlText value={bot.todays_change} /></td>
        <td className="px-3 py-2.5 text-right">
          <span className="font-mono text-sm text-zinc-200">
            {bot.open_position_count ?? 0}
          </span>
          <span className="text-zinc-500 font-mono text-xs"> / {bot.position_count ?? 0}</span>
        </td>
        <td className="px-3 py-2.5 text-right font-mono text-sm text-zinc-200">{fmtCcy(bot.portfolio_value)}</td>
        <td className="px-3 py-2.5 text-sm text-zinc-400">{bot.broker_name || "—"}</td>
      </tr>
      {expanded && <BotDetailRow account={account} bot={bot} />}
    </>
  );
}

// ─── Drill-down panel ──────────────────────────────────────────
function BotDetailRow({ account, bot }) {
  return (
    <tr className="bg-zinc-950/60 border-t border-zinc-800/60">
      <td colSpan={7} className="px-6 py-4">
        <Tabs defaultValue="open" className="w-full">
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger value="open" data-testid={`tab-open-${bot.id}`}>Open Positions</TabsTrigger>
            <TabsTrigger value="closed" data-testid={`tab-closed-${bot.id}`}>Closed Positions</TabsTrigger>
            <TabsTrigger value="orders" data-testid={`tab-orders-${bot.id}`}>Orders</TabsTrigger>
          </TabsList>

          <TabsContent value="open" className="mt-3">
            <PositionsTable accountId={account.id} botId={bot.id} status="open" testIdPrefix={`open-${bot.id}`} />
          </TabsContent>
          <TabsContent value="closed" className="mt-3">
            <PositionsTable accountId={account.id} botId={bot.id} status="closed" testIdPrefix={`closed-${bot.id}`} />
          </TabsContent>
          <TabsContent value="orders" className="mt-3">
            <OrdersTable accountId={account.id} botId={bot.id} testIdPrefix={`orders-${bot.id}`} />
          </TabsContent>
        </Tabs>
      </td>
    </tr>
  );
}

// ─── Positions Table ───────────────────────────────────────────
function PositionsTable({ accountId, botId, status, testIdPrefix }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const res = await api.get(`/speedbot-api/bots/${accountId}/${botId}/positions`, {
          params: { status, page_size: 100 },
        });
        if (!cancelled) setRows(res.data.positions || []);
      } catch (e) {
        if (!cancelled) setError(e.userMessage || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [accountId, botId, status]);

  if (loading) return <div className="py-4 text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>;
  if (error) return <div className="py-3 text-sm text-rose-300">Error: {error}</div>;
  if (rows.length === 0) return <div className="py-3 text-sm text-zinc-500">No {status} positions.</div>;

  return (
    <div className="overflow-x-auto rounded-md border border-zinc-800">
      <table className="min-w-full text-sm" data-testid={`${testIdPrefix}-table`}>
        <thead className="bg-zinc-900/60 text-zinc-400 text-[11px] uppercase tracking-wider">
          <tr>
            <th className="text-left px-3 py-2">Symbol</th>
            <th className="text-left px-3 py-2">Dir</th>
            <th className="text-right px-3 py-2">Qty</th>
            <th className="text-right px-3 py-2">Entry</th>
            <th className="text-right px-3 py-2">Exit</th>
            <th className="text-right px-3 py-2">P&amp;L</th>
            <th className="text-right px-3 py-2">%</th>
            <th className="text-left px-3 py-2">Entry Time</th>
            {status === "closed" && <th className="text-left px-3 py-2">Exit Time</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((p, idx) => (
            <tr key={p.id || idx} className="border-t border-zinc-800/60">
              <td className="px-3 py-2 font-mono text-zinc-100">{p.trading_symbol}</td>
              <td className="px-3 py-2">
                <span className={`inline-flex items-center gap-1 text-xs font-semibold ${p.direction === "LONG" ? "text-emerald-400" : "text-rose-400"}`}>
                  {p.direction === "LONG" ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                  {p.direction}
                </span>
              </td>
              <td className="px-3 py-2 text-right font-mono text-zinc-200">{p.quantity}</td>
              <td className="px-3 py-2 text-right font-mono text-zinc-200">{fmtNum(p.entry_price)}</td>
              <td className="px-3 py-2 text-right font-mono text-zinc-200">{p.exit_price ? fmtNum(p.exit_price) : "—"}</td>
              <td className="px-3 py-2 text-right"><PnlText value={p.profit_loss} /></td>
              <td className="px-3 py-2 text-right font-mono text-xs text-zinc-400">{fmtPct(p.percentage_change)}</td>
              <td className="px-3 py-2 text-xs text-zinc-400">{fmtDateTime(p.entry_time)}</td>
              {status === "closed" && <td className="px-3 py-2 text-xs text-zinc-400">{fmtDateTime(p.exit_time)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Orders Table ──────────────────────────────────────────────
function OrdersTable({ accountId, botId, testIdPrefix }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const res = await api.get(`/speedbot-api/bots/${accountId}/${botId}/orders`, { params: { page_size: 100 } });
        if (!cancelled) setRows(res.data.orders || []);
      } catch (e) {
        if (!cancelled) setError(e.userMessage || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [accountId, botId]);

  if (loading) return <div className="py-4 text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>;
  if (error) return <div className="py-3 text-sm text-rose-300">Error: {error}</div>;
  if (rows.length === 0) return <div className="py-3 text-sm text-zinc-500">No orders.</div>;

  return (
    <div className="overflow-x-auto rounded-md border border-zinc-800">
      <table className="min-w-full text-sm" data-testid={`${testIdPrefix}-table`}>
        <thead className="bg-zinc-900/60 text-zinc-400 text-[11px] uppercase tracking-wider">
          <tr>
            <th className="text-left px-3 py-2">Symbol</th>
            <th className="text-left px-3 py-2">Side</th>
            <th className="text-left px-3 py-2">Type</th>
            <th className="text-right px-3 py-2">Qty</th>
            <th className="text-right px-3 py-2">Price</th>
            <th className="text-right px-3 py-2">Limit</th>
            <th className="text-right px-3 py-2">Stop</th>
            <th className="text-left px-3 py-2">Status</th>
            <th className="text-left px-3 py-2">Tag</th>
            <th className="text-left px-3 py-2">Order Date</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((o, idx) => (
            <tr key={o.id || idx} className="border-t border-zinc-800/60">
              <td className="px-3 py-2 font-mono text-zinc-100">{o.trading_symbol}</td>
              <td className={`px-3 py-2 font-semibold text-xs ${o.direction === "BUY" ? "text-emerald-400" : "text-rose-400"}`}>{o.direction}</td>
              <td className="px-3 py-2 text-zinc-300 text-xs">{o.type}</td>
              <td className="px-3 py-2 text-right font-mono text-zinc-200">{o.quantity}</td>
              <td className="px-3 py-2 text-right font-mono text-zinc-200">{fmtNum(o.price)}</td>
              <td className="px-3 py-2 text-right font-mono text-zinc-400">{o.limit_price ? fmtNum(o.limit_price) : "—"}</td>
              <td className="px-3 py-2 text-right font-mono text-zinc-400">{o.stop_price ? fmtNum(o.stop_price) : "—"}</td>
              <td className="px-3 py-2 text-xs text-zinc-300">{o.status}</td>
              <td className="px-3 py-2 text-xs text-zinc-400">{o.tag || "—"}</td>
              <td className="px-3 py-2 text-xs text-zinc-400">{fmtDateTime(o.order_date)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────
export default function SpeedBot() {
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState(() => localStorage.getItem("speedbot_last_account") || "");
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(false);
  const [connState, setConnState] = useState("idle"); // idle | connecting | connected | error
  const [expandedBot, setExpandedBot] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [mappingsOpen, setMappingsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [syncMeta, setSyncMeta] = useState(null);  // sync-settings + last_sync_*
  const [discrepancies, setDiscrepancies] = useState([]);

  const currentAccount = accounts.find((a) => a.id === accountId);

  // Load accounts once
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/accounts");
        const list = res.data || [];
        setAccounts(list);
        if (!accountId && list.length > 0) {
          const firstWithCreds = list.find((a) => a.speedbot_credentials?.email);
          setAccountId((firstWithCreds || list[0]).id);
        }
      } catch (e) {
        toast.error(e.userMessage || "Failed to load accounts");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (accountId) localStorage.setItem("speedbot_last_account", accountId);
  }, [accountId]);

  const fetchPortfolio = useCallback(async () => {
    if (!accountId) return;
    setLoading(true); setConnState("connecting");
    try {
      const res = await api.get(`/speedbot-api/portfolio/${accountId}`);
      setPortfolio(res.data);
      setConnState("connected");
    } catch (e) {
      setConnState("error");
      toast.error(e.userMessage || "Failed to fetch portfolio");
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  // Auto-fetch when account changes
  useEffect(() => {
    if (accountId) fetchPortfolio();
  }, [accountId, fetchPortfolio]);

  const handleTestConnection = async () => {
    if (!accountId) return;
    try {
      await api.post(`/speedbot-api/test-connection/${accountId}`);
      toast.success("SpeedBot login successful");
      setConnState("connected");
      fetchPortfolio();
    } catch (e) {
      setConnState("error");
      toast.error(e.userMessage || "Login failed");
    }
  };

  const handleLogout = async () => {
    if (!accountId) return;
    try {
      await api.post(`/speedbot-api/logout/${accountId}`);
      setConnState("idle");
      setPortfolio(null);
      toast.success("Session cleared");
    } catch (e) {
      toast.error(e.userMessage || "Logout failed");
    }
  };

  const handleSyncNow = async () => {
    if (!accountId) return;
    setSyncing(true);
    setSyncResult(null);
    const acctLabel = currentAccount?.name || accountId;
    try {
      const res = await api.post(`/speedbot-api/sync/${accountId}`);
      setSyncResult(res.data);
      const { trades_created = 0, trades_linked = 0, trades_updated = 0, errors = [] } = res.data || {};
      if (errors.length) {
        toast.warning(`Sync done for ${acctLabel} with ${errors.length} error(s)`);
      } else if (trades_created || trades_linked) {
        toast.success(`Sync OK for ${acctLabel} — ${trades_created} new, ${trades_linked} linked, ${trades_updated} refreshed`);
      } else {
        toast.success(`Sync OK for ${acctLabel} — nothing new (${trades_updated} refreshed)`);
      }
      // refresh sync metadata
      fetchSyncMeta();
      fetchDiscrepancies();
    } catch (e) {
      toast.error(e.userMessage || `Sync failed for ${acctLabel}`);
    } finally {
      setSyncing(false);
    }
  };

  const fetchSyncMeta = useCallback(async () => {
    if (!accountId) return;
    try {
      const res = await api.get(`/speedbot-api/sync-settings/${accountId}`);
      setSyncMeta(res.data);
    } catch (e) {
      console.error("fetchSyncMeta failed:", e);
    }
  }, [accountId]);

  const fetchDiscrepancies = useCallback(async () => {
    if (!accountId) return;
    try {
      const res = await api.get(`/speedbot-api/discrepancies/${accountId}`, { params: { status: "OPEN" } });
      setDiscrepancies(res.data?.discrepancies || []);
    } catch (e) {
      console.error("fetchDiscrepancies failed:", e);
    }
  }, [accountId]);

  // Initial fetch + periodic refresh of sync metadata (every 30s)
  useEffect(() => {
    if (!accountId) return;
    fetchSyncMeta();
    fetchDiscrepancies();
    const t = setInterval(() => { fetchSyncMeta(); fetchDiscrepancies(); }, 30000);
    return () => clearInterval(t);
  }, [accountId, fetchSyncMeta, fetchDiscrepancies]);

  const hasCreds = !!currentAccount?.speedbot_credentials?.email;

  return (
    <div className="space-y-6" data-testid="speedbot-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-2">
            <Plug className="w-7 h-7 text-emerald-400" />
            SpeedBot Live
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Direct API integration with Aasma Venture. Portfolio, bots, positions, and orders — live.
          </p>
        </div>

        <div className="flex items-end gap-2 flex-wrap">
          <div className="min-w-[220px]">
            <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Account</label>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger data-testid="speedbot-account-select" className="bg-zinc-900 border-zinc-800 text-white">
                <SelectValue placeholder="Select account" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name} {a.speedbot_credentials?.email ? "" : " (no creds)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            data-testid="speedbot-test-connection-btn"
            onClick={handleTestConnection}
            disabled={!hasCreds || loading}
            variant="outline"
            className="border-zinc-700 bg-zinc-900 text-white hover:bg-zinc-800"
          >
            <Plug className="w-4 h-4 mr-2" /> Test Login
          </Button>

          <Button
            data-testid="speedbot-refresh-btn"
            onClick={fetchPortfolio}
            disabled={!hasCreds || loading}
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Refresh
          </Button>

          <Button
            data-testid="speedbot-sync-btn"
            onClick={handleSyncNow}
            disabled={!hasCreds || syncing}
            className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold"
            title={`Pull SpeedBot open positions for ${currentAccount?.name || "this account"} into the Trade Journal. Only the selected account is synced.`}
          >
            {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
            {currentAccount?.name ? `Sync ${currentAccount.name} to Journal` : "Sync to Journal"}
          </Button>

          <Button
            data-testid="speedbot-mappings-btn"
            onClick={() => setMappingsOpen(true)}
            disabled={!hasCreds}
            variant="outline"
            className="border-zinc-700 bg-zinc-900 text-white hover:bg-zinc-800"
          >
            <SettingsIcon className="w-4 h-4 mr-2" /> Bot Mappings
          </Button>

          <Button
            data-testid="speedbot-sync-settings-btn"
            onClick={() => setSettingsOpen(true)}
            disabled={!hasCreds}
            variant="outline"
            className="border-zinc-700 bg-zinc-900 text-white hover:bg-zinc-800"
          >
            <Clock className="w-4 h-4 mr-2" /> Auto-Sync
          </Button>

          <Button
            data-testid="speedbot-logout-btn"
            onClick={handleLogout}
            variant="ghost"
            className="text-zinc-400 hover:text-rose-300"
          >
            <LogOut className="w-4 h-4 mr-2" /> Clear Session
          </Button>
        </div>
      </div>

      {/* Connection banner */}
      {!hasCreds && currentAccount && (
        <div data-testid="speedbot-no-creds-banner" className="rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
          No SpeedBot credentials for <b>{currentAccount.name}</b>. Add them in Settings → Accounts.
        </div>
      )}

      {connState === "error" && (
        <div className="rounded-md border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200 flex items-center gap-2">
          <XCircle className="w-4 h-4" /> Unable to connect to SpeedBot. Check credentials or try Test Login.
        </div>
      )}

      {/* Auto-Sync Status Bar */}
      {hasCreds && syncMeta && (
        <AutoSyncStatusBar meta={syncMeta} discrepancyCount={discrepancies.length} />
      )}

      {connState === "connected" && portfolio && (
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-xs text-emerald-200 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" /> Connected — showing live data from SpeedBot API.
        </div>
      )}

      {/* Sync Result Panel */}
      {syncResult && (
        <SyncResultPanel
          result={syncResult}
          onOpenMappings={() => setMappingsOpen(true)}
          onDismiss={() => setSyncResult(null)}
        />
      )}

      {/* Bot Mappings Dialog */}
      {accountId && (
        <BotMappingsDialog
          open={mappingsOpen}
          onClose={() => setMappingsOpen(false)}
          accountId={accountId}
        />
      )}

      {/* Auto-Sync Settings Dialog */}
      {accountId && (
        <SyncSettingsDialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          accountId={accountId}
          onSaved={fetchSyncMeta}
        />
      )}

      {/* Discrepancies Panel */}
      {discrepancies.length > 0 && (
        <DiscrepanciesPanel
          discrepancies={discrepancies}
          onResolved={fetchDiscrepancies}
        />
      )}

      {/* Portfolio Summary */}
      {portfolio && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="portfolio-summary">
          <SummaryCard label="Today's Change" value={<PnlText value={portfolio.todays_change} />} />
          <SummaryCard label="Total P&L" value={<PnlText value={portfolio.total_profit_loss} />} />
          <SummaryCard label="Total P&L %" value={<span className="font-mono text-white">{fmtPct(portfolio.total_profit_loss_per)}</span>} />
          <SummaryCard label="Initial Capital" value={<span className="font-mono text-zinc-200">{fmtCcy(portfolio.initial_capital)}</span>} />
          <SummaryCard label="Portfolio Value" value={<span className="font-mono text-zinc-200">{fmtCcy(portfolio.portfolio_value)}</span>} />
        </div>
      )}

      {/* Bots Table */}
      {portfolio && (
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
            <div className="text-sm text-zinc-300 font-semibold">
              Bots <span className="text-zinc-500 font-normal">({portfolio.bots?.length || 0})</span>
            </div>
            <div className="text-[11px] text-zinc-500">Click a row to view positions &amp; orders</div>
          </div>
          {(portfolio.bots || []).length === 0 ? (
            <div className="p-8 text-center text-zinc-500 text-sm">No bots found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full" data-testid="bots-table">
                <thead className="bg-zinc-900/70 text-zinc-400 text-[11px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-3 py-2">Bot</th>
                    <th className="text-left px-3 py-2">Instruments</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-right px-3 py-2">Today's Change</th>
                    <th className="text-right px-3 py-2">Open / Total</th>
                    <th className="text-right px-3 py-2">Portfolio Value</th>
                    <th className="text-left px-3 py-2">Broker</th>
                  </tr>
                </thead>
                <tbody>
                  {portfolio.bots.map((bot) => (
                    <BotRow
                      key={bot.id}
                      account={currentAccount}
                      bot={bot}
                      expanded={expandedBot === bot.id}
                      onToggle={() => setExpandedBot(expandedBot === bot.id ? null : bot.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {loading && !portfolio && (
        <div className="flex items-center gap-2 text-zinc-400 text-sm py-12 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Connecting to SpeedBot…
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="mt-1 text-base">{value}</div>
    </div>
  );
}

// ─── Detected Symbols cell (for multi-symbol bots) ─────────────
function DetectedSymbolsCell({ detected, fallback, testId }) {
  const [expanded, setExpanded] = useState(false);
  const MAX_VISIBLE = 3;
  const visible = expanded ? detected : detected.slice(0, MAX_VISIBLE);
  const hidden = detected.length - visible.length;
  const isMulti = detected.length > 1;

  return (
    <div
      data-testid={testId}
      className="rounded-md border border-dashed border-zinc-700 bg-zinc-950/40 px-2 py-1.5 min-h-[36px]"
      title={isMulti
        ? `Multi-symbol bot — SpeedBot trades ${detected.length} symbols. Each position's actual symbol is used automatically.`
        : `SpeedBot is currently trading ${detected[0]}`}
    >
      <div className="flex flex-wrap gap-1 items-center">
        {visible.map((s) => (
          <span
            key={s}
            className="inline-block text-[11px] font-mono text-emerald-200 bg-emerald-500/10 border border-emerald-500/30 rounded px-1.5 py-0.5"
          >
            {s}
          </span>
        ))}
        {hidden > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="text-[10px] text-zinc-400 hover:text-zinc-200 underline-offset-2"
          >
            +{hidden} more
          </button>
        )}
        {expanded && detected.length > MAX_VISIBLE && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="text-[10px] text-zinc-500 hover:text-zinc-300"
          >
            less
          </button>
        )}
      </div>
      {isMulti ? (
        <div className="text-[10px] text-zinc-500 mt-0.5">auto-detected · read-only</div>
      ) : fallback && fallback !== detected[0] ? (
        <div className="text-[10px] text-amber-400 mt-0.5">mapping hint: {fallback}</div>
      ) : null}
    </div>
  );
}

// ─── Sync Result Panel ─────────────────────────────────────────
function SyncResultPanel({ result, onOpenMappings, onDismiss }) {
  const {
    trades_created = 0,
    trades_linked = 0,
    trades_updated = 0,
    positions_open = 0,
    bots_skipped_unmapped = [],
    bots_skipped_disabled = [],
    errors = [],
  } = result || {};

  const hasUnmapped = bots_skipped_unmapped.length > 0;
  const hasErrors = errors.length > 0;

  return (
    <div data-testid="sync-result-panel" className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-amber-400" />
          <div>
            <div className="text-sm font-semibold text-white">Sync Result</div>
            <div className="text-xs text-zinc-400">{positions_open} open position(s) processed</div>
          </div>
        </div>
        <button onClick={onDismiss} className="text-zinc-500 hover:text-zinc-300 text-xs">Dismiss</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3">
        <ResultStat label="Created" value={trades_created} accent="emerald" />
        <ResultStat label="Linked" value={trades_linked} accent="sky" />
        <ResultStat label="Refreshed" value={trades_updated} accent="zinc" />
        <ResultStat label="Closed" value={result.trades_closed || 0} accent="violet" />
        <ResultStat label="Errors" value={errors.length} accent={hasErrors ? "rose" : "zinc"} />
      </div>

      {hasUnmapped && (
        <div className="mt-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200">
          <div className="flex items-center gap-2 mb-1.5 font-semibold">
            <AlertTriangle className="w-4 h-4" /> {bots_skipped_unmapped.length} bot(s) skipped — needs mapping
          </div>
          <ul className="ml-6 list-disc space-y-0.5">
            {bots_skipped_unmapped.map((b) => (
              <li key={b.bot_id}>
                <span className="font-mono text-amber-100">{b.bot_name}</span>
                {b.suggested_symbol && <span className="text-amber-300/70"> · suggested: {b.suggested_symbol}</span>}
              </li>
            ))}
          </ul>
          <button
            onClick={onOpenMappings}
            className="mt-2 text-amber-300 hover:text-amber-200 underline underline-offset-2"
          >
            Open Bot Mappings →
          </button>
        </div>
      )}

      {bots_skipped_disabled.length > 0 && (
        <div className="mt-2 text-xs text-zinc-500">
          {bots_skipped_disabled.length} bot(s) disabled in mappings (intentionally skipped).
        </div>
      )}

      {hasErrors && (
        <div className="mt-3 rounded-md border border-rose-500/30 bg-rose-500/5 p-3 text-xs text-rose-200">
          <div className="font-semibold mb-1">Errors</div>
          <ul className="ml-6 list-disc space-y-0.5">
            {errors.slice(0, 5).map((e, i) => (<li key={`${e}-${i}`}>{e}</li>))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ResultStat({ label, value, accent }) {
  const palette = {
    emerald: "border-emerald-500/30 bg-emerald-500/10 text-emerald-200",
    sky:     "border-sky-500/30 bg-sky-500/10 text-sky-200",
    violet:  "border-violet-500/30 bg-violet-500/10 text-violet-200",
    zinc:    "border-zinc-700 bg-zinc-900 text-zinc-300",
    rose:    "border-rose-500/30 bg-rose-500/10 text-rose-200",
  }[accent] || "border-zinc-700 bg-zinc-900 text-zinc-300";
  return (
    <div className={`rounded-md border px-3 py-2 ${palette}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-2xl font-mono font-bold">{value}</div>
    </div>
  );
}

// ─── Bot Mappings Dialog ───────────────────────────────────────
function BotMappingsDialog({ open, onClose, accountId }) {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const m = await api.get(`/speedbot-api/bot-mappings/${accountId}`);
      setMappings(m.data?.mappings || []);
    } catch (e) {
      toast.error(e.userMessage || "Failed to load mappings");
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const updateField = (botId, patch) => {
    setMappings((prev) => prev.map((m) => (m.bot_id === botId ? { ...m, ...patch } : m)));
  };

  const save = async (mapping) => {
    setSaving(mapping.bot_id);
    try {
      await api.put(`/speedbot-api/bot-mappings/${accountId}/${mapping.bot_id}`, {
        strategy_id: mapping.strategy_id || null,  // preserved if previously set, never edited via UI
        strategy_name: mapping.strategy_name || null,
        symbol: mapping.symbol || null,
        exchange: mapping.exchange || null,
        enabled: true,  // saving = activate; sync runs for any saved mapping
      });
      toast.success(`Saved: ${mapping.bot_name}`);
      updateField(mapping.bot_id, { is_unsaved: false, enabled: true });
    } catch (e) {
      toast.error(e.userMessage || "Save failed");
    } finally {
      setSaving(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="bot-mappings-dialog"
        className="bg-zinc-950 border-zinc-800 text-white max-w-4xl max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BotIcon className="w-5 h-5 text-emerald-400" /> SpeedBot → Strategy Mappings
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            For each SpeedBot bot, set the <b>Symbol Name</b>, <b>Exchange</b>, and <b>Strategy Name</b>. Click <b>Save</b> to activate auto-sync for that bot. Bots without a saved mapping are skipped.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 flex justify-center text-zinc-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : (
          <div className="space-y-2">
            {mappings.length === 0 && <div className="text-zinc-500 text-sm py-8 text-center">No bots found.</div>}
            {mappings.map((m) => (
              <div
                key={m.bot_id}
                data-testid={`mapping-row-${m.bot_id}`}
                className="grid grid-cols-12 gap-3 items-center rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2.5"
              >
                <div className="col-span-3">
                  <div className="text-sm font-semibold text-white truncate">{m.bot_name}</div>
                  <div className="text-[11px] text-zinc-500 font-mono">id: {m.bot_id}</div>
                  {m.open_position_count != null && m.open_position_count > 0 && (
                    <div className="text-[11px] text-emerald-400 mt-0.5">{m.open_position_count} open</div>
                  )}
                </div>

                <div className="col-span-3">
                  {Array.isArray(m.detected_symbols) && m.detected_symbols.length > 0 ? (
                    <DetectedSymbolsCell
                      detected={m.detected_symbols}
                      fallback={m.symbol}
                      testId={`detected-symbols-${m.bot_id}`}
                    />
                  ) : (
                    <>
                      <Input
                        data-testid={`mapping-symbol-${m.bot_id}`}
                        value={m.symbol || ""}
                        placeholder="Symbol Name"
                        onChange={(e) => updateField(m.bot_id, { symbol: e.target.value, is_unsaved: true })}
                        className="bg-zinc-950 border-zinc-800 h-9 text-sm"
                      />
                      <div className="text-[10px] text-zinc-500 mt-0.5">
                        No positions yet — actual symbols will auto-detect on first trade
                      </div>
                    </>
                  )}
                </div>

                <div className="col-span-2">
                  <Select
                    value={m.exchange || ""}
                    onValueChange={(v) => updateField(m.bot_id, { exchange: v, is_unsaved: true })}
                  >
                    <SelectTrigger data-testid={`mapping-exchange-${m.bot_id}`} className="bg-zinc-950 border-zinc-800 h-9 text-sm">
                      <SelectValue placeholder="Exchange" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                      <SelectItem value="NSE">NSE</SelectItem>
                      <SelectItem value="NFO">NFO</SelectItem>
                      <SelectItem value="MCX">MCX</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="col-span-3">
                  <Input
                    data-testid={`mapping-strategy-name-${m.bot_id}`}
                    value={m.strategy_name || ""}
                    placeholder="Strategy Name"
                    onChange={(e) => updateField(m.bot_id, { strategy_name: e.target.value, is_unsaved: true })}
                    className="bg-zinc-950 border-zinc-800 h-9 text-sm"
                  />
                </div>

                <div className="col-span-1 flex justify-end">
                  <Button
                    data-testid={`mapping-save-${m.bot_id}`}
                    size="sm"
                    onClick={() => save(m)}
                    disabled={saving === m.bot_id}
                    className={`h-9 text-xs ${m.is_unsaved || !m.enabled ? "bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold" : "bg-zinc-800 text-zinc-400"}`}
                  >
                    {saving === m.bot_id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-zinc-700 bg-zinc-900 text-white hover:bg-zinc-800">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Auto-Sync Status Bar ──────────────────────────────────────
function AutoSyncStatusBar({ meta, discrepancyCount }) {
  const { settings, last_sync_at, last_sync_status, last_sync_error, last_sync_summary } = meta || {};
  const enabled = !!settings?.enabled;

  const since = (() => {
    if (!last_sync_at) return "never";
    try {
      const sec = Math.floor((Date.now() - new Date(last_sync_at).getTime()) / 1000);
      if (sec < 60) return `${sec}s ago`;
      if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
      return `${Math.floor(sec / 3600)}h ago`;
    } catch { return last_sync_at; }
  })();

  const dotColor =
    last_sync_status === "ERROR" ? "bg-rose-500"
    : last_sync_status === "PARTIAL" ? "bg-amber-400"
    : enabled ? "bg-emerald-500"
    : "bg-zinc-600";

  return (
    <div data-testid="autosync-status-bar" className="rounded-md border border-zinc-800 bg-zinc-900/40 px-4 py-2.5 flex items-center gap-4 flex-wrap">
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${dotColor} ${enabled ? "shadow-[0_0_6px_currentColor]" : ""}`} />
        <span className="text-xs uppercase tracking-wider text-zinc-400">
          Auto-Sync: <b className={enabled ? "text-emerald-300" : "text-zinc-500"}>{enabled ? "ON" : "OFF"}</b>
        </span>
      </div>
      <div className="text-xs text-zinc-400">
        Every <b className="text-white">{settings?.interval_minutes ?? 5}m</b>
        {settings?.only_during_market_hours && " · market-hours only"}
      </div>
      <div className="text-xs text-zinc-400">
        Last sync: <span className="font-mono text-white">{since}</span>
        {last_sync_summary && (
          <span className="ml-2 text-zinc-500">
            ({last_sync_summary.created} new, {last_sync_summary.linked} linked, {last_sync_summary.updated} refreshed)
          </span>
        )}
      </div>
      {last_sync_error && (
        <div className="text-xs text-rose-300 flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> {last_sync_error}
        </div>
      )}
      {discrepancyCount > 0 && (
        <div className="ml-auto text-xs text-amber-300 flex items-center gap-1">
          <AlertTriangle className="w-3.5 h-3.5" />
          <b>{discrepancyCount}</b> open {discrepancyCount > 1 ? "discrepancies" : "discrepancy"}
        </div>
      )}
    </div>
  );
}

// ─── Sync Settings Dialog ──────────────────────────────────────
function SyncSettingsDialog({ open, onClose, accountId, onSaved }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await api.get(`/speedbot-api/sync-settings/${accountId}`);
        setSettings(res.data?.settings);
      } catch (e) {
        toast.error(e.userMessage || "Failed to load settings");
      }
    })();
  }, [open, accountId]);

  const update = (patch) => setSettings((s) => ({ ...s, ...patch }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/speedbot-api/sync-settings/${accountId}`, settings);
      toast.success("Auto-sync settings saved");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e.userMessage || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="sync-settings-dialog"
        className="bg-zinc-950 border-zinc-800 text-white max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-emerald-400" /> Auto-Sync Settings
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Configure how often SpeedBot positions sync into your Trade Journal.
          </DialogDescription>
        </DialogHeader>

        {!settings ? (
          <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-white">Enable auto-sync</div>
                <div className="text-xs text-zinc-500">Run sync in the background on a schedule</div>
              </div>
              <Switch
                data-testid="setting-enabled"
                checked={!!settings.enabled}
                onCheckedChange={(v) => update({ enabled: v })}
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">
                Interval (minutes) — <b className="text-white">{settings.interval_minutes}m</b>
              </label>
              <Input
                data-testid="setting-interval"
                type="number"
                min="1"
                max="60"
                value={settings.interval_minutes}
                onChange={(e) => update({ interval_minutes: Math.max(1, Math.min(60, parseInt(e.target.value, 10) || 5)) })}
                className="bg-zinc-900 border-zinc-800"
              />
              <p className="text-[11px] text-zinc-500 mt-1">Range 1–60 minutes. Default 5.</p>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-white">Only during market hours</div>
                <div className="text-xs text-zinc-500">Skip sync when NSE & MCX are both closed</div>
              </div>
              <Switch
                data-testid="setting-market-hours"
                checked={!!settings.only_during_market_hours}
                onCheckedChange={(v) => update({ only_during_market_hours: v })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-white">Broker cross-check</div>
                <div className="text-xs text-zinc-500">Match each trade against your AngelOne tradebook (0.5% tolerance)</div>
              </div>
              <Switch
                data-testid="setting-broker-check"
                checked={!!settings.broker_cross_check_enabled}
                onCheckedChange={(v) => update({ broker_cross_check_enabled: v })}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-zinc-700 bg-zinc-900 text-white hover:bg-zinc-800">Cancel</Button>
          <Button
            data-testid="setting-save-btn"
            onClick={handleSave}
            disabled={!settings || saving}
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Discrepancies Panel ───────────────────────────────────────
function DiscrepanciesPanel({ discrepancies, onResolved }) {
  const handleResolve = async (id) => {
    try {
      await api.post(`/speedbot-api/discrepancies/${id}/resolve`);
      toast.success("Marked as resolved");
      onResolved && onResolved();
    } catch (e) {
      toast.error(e.userMessage || "Resolve failed");
    }
  };

  return (
    <div data-testid="discrepancies-panel" className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
      <div className="flex items-center gap-2 mb-2">
        <Activity className="w-5 h-5 text-amber-400" />
        <div className="text-sm font-semibold text-amber-200">Broker Discrepancies ({discrepancies.length})</div>
      </div>
      <p className="text-xs text-amber-100/70 mb-3 leading-relaxed">
        Positions found on <b>SpeedBot</b> that could not be matched against your <b>AngelOne tradebook</b> for the same day. Most often this means the broker order didn't actually fill (or filled in a different account / quantity), so the trade in your journal may not have a real broker counterpart.
        <br />
        <span className="text-amber-300 font-mono">MISSING_IN_BROKER</span> = SpeedBot recorded the trade, but no matching BUY/SELL was found in AngelOne tradebook for the same symbol, side and quantity. <b>SB</b> = SpeedBot fill price, <b>BR</b> = Broker fill price. Click <b>Resolve</b> after you've reviewed it (e.g., the broker order was actually placed under another account, or it's a forward-test bot).
      </p>
      <div className="space-y-2">
        {discrepancies.map((d) => (
          <div key={d.id} data-testid={`discrepancy-${d.id}`} className="grid grid-cols-12 gap-2 items-center text-xs rounded-md bg-zinc-950/60 border border-amber-500/20 px-3 py-2">
            <div className="col-span-2 font-mono text-amber-200">{d.symbol} <span className="text-zinc-500">{d.direction}</span></div>
            <div className="col-span-2"><span className="rounded bg-rose-500/20 text-rose-200 px-1.5 py-0.5">{d.kind}</span></div>
            <div className="col-span-2 text-zinc-300 font-mono">SB: {d.speedbot_price ?? "—"}</div>
            <div className="col-span-2 text-zinc-300 font-mono">BR: {d.broker_price ?? "—"}</div>
            <div className="col-span-2 text-zinc-500 truncate" title={d.details || ""}>{d.details || ""}</div>
            <div className="col-span-2 flex justify-end">
              <Button
                size="sm"
                onClick={() => handleResolve(d.id)}
                className="h-7 text-xs bg-zinc-800 hover:bg-zinc-700 text-white"
                data-testid={`resolve-${d.id}`}
              >
                Resolve
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
